import { Video } from "../pages/content/types";
import { findImageExtensions } from "../utils/commons";
import http from "../utils/http";

interface GetCreateVideoParams {
  title: string;
  description: string;
  url: string;
  thumbnailUrl: string;
  type: string;
  duration: number;
  sourceVideoId: string;
}

interface GetUpdateVideoParams {
  title: string;
  description: string;
  url: string;
  type: string;
  duration: number;
  thumbnailUrl: string;
  sourceVideoId: string;
  id: string;
}

interface SourceVideoDetails {
  videoId: string;
  playbackUrl: string;
  duration: number;
  createdAt: string;
  width: number;
  height: number;
}

interface ApiErrorResponse {
  message?: string;
  debugMessage?: string;
  statusCode?: number;
}

interface RequestError {
  message?: string;
  response?: {
    status?: number;
    data?: ApiErrorResponse;
  };
}

export const getSourceVideoDetails = async ({
  videoId,
}: {
  videoId: string;
}): Promise<SourceVideoDetails> => {
  const path = `/admin/videos/source/${videoId}`;
  const response = await http.get({ path });

  if (response.status !== 200) {
    throw new Error("Failed to fetch video details");
  }

  return response.data?.data;
};

export const getSourceVideoDetailsErrorMessage = (error: unknown): string => {
  const requestError = error as RequestError;
  const status = requestError.response?.status;
  const apiError = requestError.response?.data;

  if (status === 404) {
    return apiError?.debugMessage || "Source video was not found";
  }

  return (
    apiError?.debugMessage ||
    apiError?.message ||
    requestError.message ||
    "Failed to fetch video details"
  );
};

export const createVideo = async ({
  title,
  description,
  url,
  type,
  duration,
  thumbnailUrl,
  sourceVideoId,
}: GetCreateVideoParams) => {
  const path = "/admin/videos";
  const body = {
    title,
    description,
    url,
    type: type == "reel" ? "VERTICAL" : "HORIZONTAL",
    duration,
    thumbnailUrl,
    sourceVideoId,
  };

  const result = await http.post({ path, body });
  return result;
};

export const updateVideoCall = async ({
  title,
  description,
  url,
  type,
  duration,
  thumbnailUrl,
  sourceVideoId,
  id,
}: GetUpdateVideoParams) => {
  const path = `/admin/videos`;
  const body = {
    title,
    description,
    url,
    type: type == "reel" ? "VERTICAL" : "HORIZONTAL",
    duration,
    thumbnailUrl,
    sourceVideoId,
    id,
  };

  const result = await http.put({ path, body });
  return result;
};

export const uploadVideoThumbnail = async (name: string) => {
  const urlPath = `/admin/videos/upload-thumbnail?fileName=${name}`;
  const GetSignUrl = await http.get({ path: urlPath });
  return GetSignUrl;
};

export const sendPutRequest = async ({
  signUrl,
  file,
}: {
  signUrl: string;
  file: File;
}): Promise<Response> => {
  const headers = {
    "Content-Type": `image/${findImageExtensions(file.name)}`,
    // "x-amz-acl": "public-read",
  };

  const options: RequestInit = {
    method: "PUT",
    headers: headers,
    body: file,
  };

  const response = await fetch(signUrl, options);
  return response;
};

export const getVideoList = async ({
  page,
  size,
}: {
  page: number;
  size: number;
}): Promise<any> => {
  try {
    const urlPath = `/admin/videos?page=${page}&size=${size}`;
    const response = await http.get({ path: urlPath });
    return response;
  } catch (error) {
    throw new Error("Failed to fetch video list");
  }
};

export const getSingleVideo = async (selectedVideo: string): Promise<Video> => {
  const urlPath = `/admin/videos/${selectedVideo}`;

  const result = await http.get({ path: urlPath });
  if (result.status == 200) {
    return result.data;
  } else {
    throw new Error("Failed to fetch video Details");
  }
};

export const getPlayList = async ({
  page,
  size,
}: {
  page: number;
  size: number;
}): Promise<any> => {
  try {
    const urlPath = `/admin/playlists?page=${page}&size=${size}`;
    const result = await http.get({ path: urlPath });
    return result;
  } catch (error) {
    return error as Error;
  }
};

export const createPlaylist = async ({
  name,
  description,
  thumbnailUrl,
  availableForVisitors,
}: {
  name: string;
  description: string;
  thumbnailUrl: string;
  availableForVisitors: boolean;
}): Promise<number> => {
  try {
    const urlPath = `/admin/playlists`;
    const body = {
      name,
      description,
      thumbnailUrl,
      availableForVisitors,
    };
    const result = await http.post({ path: urlPath, body });
    return result.status;
  } catch (error) {
    return 500;
  }
};

export interface Playlist {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  videos: Video[];
  createdAt: string;
  updatedAt: string;
  availableForVisitors: boolean;
}

export const GetSinglePlayList = async ({
  selectedPlayList,
}: {
  selectedPlayList: string;
}): Promise<Playlist> => {
  const urlPath = `/admin/playlists/${selectedPlayList}`;

  try {
    const result = await http.get({ path: urlPath });
    if (result.status === 200) {
      return result.data;
    } else {
      throw new Error("Failed to fetch playlist");
    }
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error fetching playlist: ${error.message}`);
    } else {
      throw new Error("An unknown error occurred");
    }
  }
};

interface UpdatePlaylistParams {
  name: string;
  description: string;
  thumbnailUrl: string;
  availableForVisitors: boolean;
  id: string;
}

export const updatePlaylist = async ({
  name,
  description,
  thumbnailUrl,
  availableForVisitors,
  id,
}: UpdatePlaylistParams): Promise<number> => {
  const urlPath = `/admin/playlists`;

  try {
    const response = await http.put({
      path: urlPath,
      body: { name, description, thumbnailUrl, availableForVisitors, id },
    });

    if (response.status == 204) {
      return response.status;
    } else {
      throw new Error(`Error updating playlist:`);
    }
  } catch (error) {
    console.error("Error updating playlist:", error);
    if (error instanceof Error) {
      throw new Error(`Error updating playlist: ${error.message}`);
    } else {
      throw new Error("An unknown error occurred");
    }
  }
};

export const AddVideoTolist = async ({
  playlistId,
  videoId,
}: {
  playlistId: string;
  videoId: string;
}): Promise<number> => {
  const urlPath = "/admin/playlists/add-video";
  const result = await http.put({
    path: urlPath,
    body: { playlistId, videoId },
  });

  if (result.status == 200) {
    return 200;
  } else {
    return 401;
  }
};

export const RemoveVideoFromList = async ({
  playlistId,
  videoId,
}: {
  playlistId: string;
  videoId: string;
}): Promise<number> => {
  const urlPath = "/admin/playlists/remove-video";
  const result = await http.put({
    path: urlPath,
    body: { playlistId, videoId },
  });
  if (result.status == 200) {
    return 200;
  } else {
    return 401;
  }
};

export const DeleteVideo = async ({ id }: { id: string }): Promise<number> => {
  const urlPath = `/admin/videos?videoId=${id}`;
  const result = await http.deleteHttp({ path: urlPath, body: {} });
  if (result.status == 200) {
    return 200;
  } else {
    return 401;
  }
};
